<script setup>
import LaisField from 'dashboard/components-next/LaisField.vue';
import { CATEGORIES, PROPERTY_TYPES, TRANSACTION_TYPES } from '../constants';

const props = defineProps({
  controller: { type: Object, required: true },
});

const input =
  'w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-lais-border rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50 placeholder:text-lais-text-muted transition-all';

const onTipoChange = value => {
  props.controller.updateState({ tipoImovel: value, subTipoImovel: '' });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="mx-auto flex w-full max-w-3xl flex-col gap-5">
    <div
      class="rounded-lais bg-lais-bg-soft px-4 py-3 text-[12px] text-lais-text-muted"
    >
      Seu rascunho é salvo automaticamente enquanto você ajusta os detalhes.
    </div>

    <!-- Identificação -->
    <div class="flex flex-col gap-4">
      <LaisField label="Título" required helper="De 10 a 100 caracteres">
        <input
          :value="controller.state.titulo"
          maxlength="100"
          placeholder="Apartamento com varanda gourmet e vista aberta"
          :class="input"
          @input="controller.updateState({ titulo: $event.target.value })"
        />
        <span class="text-[11px] text-lais-text-muted">
          {{ controller.state.titulo.length }}/100
        </span>
      </LaisField>

      <LaisField label="Descrição" required helper="Mínimo de 50 caracteres">
        <textarea
          :value="controller.state.descricao"
          maxlength="3000"
          rows="4"
          placeholder="Descreva o imóvel, diferenciais, localização..."
          class="min-h-[96px] resize-y"
          :class="[input]"
          @input="controller.updateState({ descricao: $event.target.value })"
        />
        <span class="text-[11px] text-lais-text-muted">
          {{ controller.state.descricao.length }}/3000
        </span>
      </LaisField>
    </div>

    <!-- Tipo e transação -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <LaisField label="Tipo do imóvel" required>
        <select
          :value="controller.state.tipoImovel"
          :class="input"
          @change="onTipoChange($event.target.value)"
        >
          <option value="">Selecione...</option>
          <option
            v-for="item in PROPERTY_TYPES"
            :key="item.tipo"
            :value="item.tipo"
          >
            {{ item.tipo }}
          </option>
        </select>
      </LaisField>

      <LaisField v-if="controller.state.tipoImovel" label="Subtipo" required>
        <select
          :value="controller.state.subTipoImovel"
          :class="input"
          @change="
            controller.updateState({ subTipoImovel: $event.target.value })
          "
        >
          <option value="">Selecione...</option>
          <option
            v-for="sub in controller.getSubtipos()"
            :key="sub"
            :value="sub"
          >
            {{ sub }}
          </option>
        </select>
      </LaisField>

      <LaisField label="Categoria">
        <select
          :value="controller.state.categoriaImovel"
          :class="input"
          @change="
            controller.updateState({ categoriaImovel: $event.target.value })
          "
        >
          <option v-for="cat in CATEGORIES" :key="cat" :value="cat">
            {{ cat }}
          </option>
        </select>
      </LaisField>
    </div>

    <LaisField label="Transação" required>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="item in TRANSACTION_TYPES"
          :key="item"
          type="button"
          class="flex-1 rounded-lais-sm border px-3 py-2 text-[13px] font-medium transition-all"
          :class="
            controller.state.transactionType === item
              ? 'border-lais-primary bg-lais-primary-50 font-semibold text-lais-primary'
              : 'border-lais-border text-lais-text-muted hover:border-lais-primary'
          "
          @click="controller.setTransaction(item)"
        >
          {{ item }}
        </button>
      </div>
    </LaisField>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <LaisField
        v-if="controller.state.transactionType !== 'Aluguel'"
        label="Preço de venda (R$)"
        required
      >
        <input
          type="number"
          min="0"
          :value="controller.state.precoVenda"
          placeholder="850000"
          :class="input"
          @input="controller.updateState({ precoVenda: $event.target.value })"
        />
      </LaisField>

      <LaisField
        v-if="controller.state.transactionType !== 'Venda'"
        label="Preço de aluguel (R$)"
        required
      >
        <input
          type="number"
          min="0"
          :value="controller.state.precoAluguel"
          placeholder="4500"
          :class="input"
          @input="controller.updateState({ precoAluguel: $event.target.value })"
        />
      </LaisField>
    </div>

    <!-- Dimensões -->
    <div class="flex flex-col gap-4">
      <p class="text-[14px] font-semibold text-lais-text-body">Dimensões</p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <LaisField
          label="Área útil (m²)"
          :required="controller.state.tipoImovel !== 'Terreno'"
        >
          <input
            type="number"
            min="0"
            :value="controller.state.areaUtil"
            placeholder="120"
            :class="input"
            @input="controller.updateState({ areaUtil: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Área total (m²)">
          <input
            type="number"
            min="0"
            :value="controller.state.areaTotal"
            placeholder="200"
            :class="input"
            @input="controller.updateState({ areaTotal: $event.target.value })"
          />
        </LaisField>
      </div>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <LaisField label="Quartos">
          <input
            type="number"
            min="0"
            :value="controller.state.qtdDormitorios"
            placeholder="3"
            :class="input"
            @input="
              controller.updateState({ qtdDormitorios: $event.target.value })
            "
          />
        </LaisField>
        <LaisField label="Suítes">
          <input
            type="number"
            min="0"
            :value="controller.state.qtdSuites"
            placeholder="1"
            :class="input"
            @input="controller.updateState({ qtdSuites: $event.target.value })"
          />
        </LaisField>
        <LaisField label="Banheiros">
          <input
            type="number"
            min="0"
            :value="controller.state.qtdBanheiros"
            placeholder="2"
            :class="input"
            @input="
              controller.updateState({ qtdBanheiros: $event.target.value })
            "
          />
        </LaisField>
        <LaisField label="Vagas">
          <input
            type="number"
            min="0"
            :value="controller.state.qtdVagas"
            placeholder="2"
            :class="input"
            @input="controller.updateState({ qtdVagas: $event.target.value })"
          />
        </LaisField>
      </div>
    </div>
  </div>
</template>
